import React from 'react';
import {
  Box,
  Typography,
  Grid2,
  TextField,
  Button,
  Stack
} from '@mui/material';
import HeaderProfile from '../../../../components/header_Profile/header_Profile.index';
import SummaryCard from '../Package_Summary_Card';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAlert } from '../../../../components/alertProvider';
import { RefundRequestPayload, RequestRefundinitialValues, requestRefundItem, RequestRefundvalidationSchema, postrefundRequestURL } from '../advance.helper';
import { Formik, Form } from 'formik';
import { ConfirmationModal } from '../../../../components/confirmationModal';
import { postRequest } from '../../../../services/httpService';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrency } from '../../../../components/CurrencyProvider';
import { usePatientContext } from '../../../../components/SubPatinetProvider';
import { useTranslation } from 'react-i18next';


const RequestRefund: React.FC = () => {
  const location = useLocation();
  const { addAlert } = useAlert();
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  const [openModal1, setOpenModal1] = React.useState(false);
  const [pendingValues, setPendingValues] = React.useState<RefundRequestPayload | null>(null);
  const queryClient = useQueryClient();
  const { patientId } = usePatientContext();
  const { t : tBilling } = useTranslation('billing_config');
  
  // Prefer useMutation for POST requests
  const { mutate: submitRefund, isPending: isPosting } = useMutation({
    mutationFn: (payload: RefundRequestPayload) => postRequest<RefundRequestPayload>(postrefundRequestURL(), payload),
    onSuccess: async () => {
      addAlert(tBilling('Advance.messages.refund_request_sent_success', { defaultValue: 'Refund request sent successfully.' }), 'success');
      setOpenModal1(false);
      // Invalidate received list so it refetches with latest data
      await queryClient.invalidateQueries({ queryKey: ['ReceivedAdvanceList'] });
      navigate(`/admin/billing/advance/patient-advance/${patientId}?Logs`);
    },
    onError: (err: unknown) => {
      const message = (err as { message?: string })?.message || tBilling('Advance.messages.refund_request_send_failed', { defaultValue: 'Failed to send refund request.' });
      addAlert(message, 'error');
    },
  });
  

  const summary: requestRefundItem | null = location.state && typeof location.state === 'object' && 'summary' in location.state
    ? (location.state as { summary: requestRefundItem }).summary
    : null;



  const handleSubmit = (values: typeof RequestRefundinitialValues) => {
    // Build payload as per requirements
    const payload = {
      patient_id: values.patient_id,
      advance_type: values.advance_type,
      advance_against: values.advance_against,
      amount: values.amount,
      remark: values.remark,
      credit_transaction_id: values.credit_transaction_id,
      ...(values.advance_type === 'PACKAGE' && values.patient_package_id ? { patient_package_id: values.patient_package_id } : {})
    };
    submitRefund(payload);
  };

  const handleCloseModal1 = () => { setOpenModal1(false); };
  return (
    <>
      <Box sx={{ p: 0 }}>
        <HeaderProfile />
      </Box><Box sx={{ p: 3 }} data-testid="request-refund-container">
        <Typography variant="h6" fontWeight={700} mb={3}>
          {tBilling('Advance.refund_request')}
        </Typography>

        <SummaryCard summary={summary} />
        <Formik
          initialValues={
            {
              ...RequestRefundinitialValues,
              patient_id: summary?.patient_id ?? 0,
              patient_package_id: summary?.patient_package_id ?? null,
              advance_type: summary?.patient_package_id ? 'PACKAGE' : 'CHECKIN',
              advance_against: summary?.advance_against ?? '',
              totalAdvance: summary?.total_amount ?? 0,
              advanceConsumed: summary?.used_amount ?? 0,
              credit_transaction_id: summary?.credit_transaction_id ?? null,
              totalRefund: summary?.refund_amount ?? 0,
              advanceAvailableForRefund: summary?.balance_amount ?? 0,
            }}
          validationSchema={RequestRefundvalidationSchema}
          onSubmit={(vals) => { setPendingValues(vals); setOpenModal1(true); }}

        >
          {({ values, handleChange, handleBlur, touched, errors }) => {          
            return(
              <Form>
                <Grid2 container spacing={2} sx={{ my: 2 }}> 
                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <TextField
                      label={tBilling('Advance.refund_amount')}
                      name="amount"
                      type='number'
                      size='small'
                      value={values.amount}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      slotProps={{ inputLabel: { shrink: true } }}
                      error={touched.amount && Boolean(errors.amount)}
                      helperText={touched.amount && errors.amount}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <TextField
                      label={tBilling('Advance.total_advance')}
                      size='small'
                      name="totalAdvance"
                      value={formatCurrency(values?.totalAdvance ?? 0)}
                      fullWidth
                      slotProps={{ inputLabel: { shrink: true }, input: { readOnly: true } }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <TextField
                      label={tBilling('Advance.advance_consumed')}
                      size='small'
                      name="advanceConsumed"
                      value={(values?.advanceConsumed ?? 0).toFixed(2)}
                      fullWidth
                      slotProps={{ inputLabel: { shrink: true }, input: { readOnly: true } }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <TextField
                      size='small'
                      label={tBilling('Advance.total_refund')}
                      name="totalRefund"
                      value={(values?.totalRefund ?? 0).toFixed(2)}
                      fullWidth
                      slotProps={{ inputLabel: { shrink: true }, input: { readOnly: true } }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <TextField
                      label={tBilling('Advance.advance_available_for_refund')}
                      size='small'
                      name="advanceAvailableForRefund"
                      value={formatCurrency((values?.advanceAvailableForRefund ?? 0) - (values?.amount ?? 0))}
                      fullWidth
                      slotProps={{ inputLabel: { shrink: true }, input: { readOnly: true } }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 4, lg:6 }}>
                    <TextField
                      label={tBilling('Advance.remark')}
                      size='small'
                      name="remark"
                      value={values.remark}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder={tBilling('Advance.type_here')}
                      fullWidth
                      slotProps={{ inputLabel: { shrink: true } }}
                      multiline
                      minRows={1}
                      error={touched.remark && Boolean(errors.remark)}
                      helperText={touched.remark && errors.remark}
                    />
                  </Grid2>
                </Grid2>
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button variant="outlined" sx={{ minWidth: 120, borderRadius: 2 }} type="button" onClick={() => navigate(`/admin/billing/advance/patient-advance/${patientId}?Logs`)}>
                    {tBilling('package_details.cancel')}
                  </Button>
                  <Button variant="contained" sx={{ minWidth: 160, borderRadius: 2 }} type="submit" disabled={isPosting}>
                    {tBilling('Advance.send_request')}
                  </Button>
                </Stack>

                <ConfirmationModal
                  open={openModal1}
                  handleClose={handleCloseModal1}
                  onConfirm={() => { if (pendingValues) handleSubmit(pendingValues); }}
                  type={'save'}
                  title={tBilling('Advance.refund_request')}
                  contentMessage={tBilling('Advance.confirm_save_refund_request')}
                  isConfirming={isPosting}
                />
              </Form>
            );}
          }
        </Formik>
      </Box></>
  );
};

export default RequestRefund;
